/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout, TextStyle, Link, Icon } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { POSITION_OPTIONS, POSITION_VALUE, MENU_ID } from 'src/constants/theme-settings.jsx';
import {
  COLLECTION_HEADER_VALUE,
  LAYOUT_OPTIONS,
  ELEMENTS,
  IMAGE_SIZE_OPTIONS,
  PARALLAX_DIRECTION_OPTIONS
} from 'src/constants/theme-settings.js';
import { URL } from 'src/constants/links';
import { DropdownIndicator, styles } from './react-select-style';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class ProductListCollectionHeaderForm extends Component<ComposedProps> {
  state = {
    isOpenedCheckout: true,
    isOpenedViewCart: true
  };

  allowEnabled = (position: string) => {
    const { formValues } = this.props;
    const layout = _.get(formValues, `setting.${MENU_ID.COLLECTION_HEADER}.layout`);

    // Show all
    if (
      [
        COLLECTION_HEADER_VALUE.COLLECTION_HEADER_2,
        COLLECTION_HEADER_VALUE.COLLECTION_HEADER_4
      ].includes(layout)
    ) {
      return true;
    }

    // Only show middle-left and middle-right
    if (layout === COLLECTION_HEADER_VALUE.COLLECTION_HEADER_1) {
      return [POSITION_VALUE.MIDDLE_LEFT, POSITION_VALUE.MIDDLE_RIGHT].includes(position);
    }

    // Only show all button in the middle
    if (layout === COLLECTION_HEADER_VALUE.COLLECTION_HEADER_3) {
      return [
        POSITION_VALUE.MIDDLE_LEFT,
        POSITION_VALUE.MIDDLE_RIGHT,
        POSITION_VALUE.MIDDLE_CENTER
      ].includes(position);
    }

    // Show all
    return true;
  };

  /**
   * Get breadcrumb position as a grid radio button
   */
  getPositions = () => {
    const { i18n, formValues } = this.props;
    const layout = _.get(formValues, `setting.${MENU_ID.COLLECTION_HEADER}.layout`);

    // Requirement from BA (Angeline)
    if (layout === COLLECTION_HEADER_VALUE.COLLECTION_HEADER_3) {
      return null;
    }

    return (
      <>
        <p className="form-section__item form-section__item--standard-spacing">
          {i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeader_ContentPosition_Label')}
        </p>
        <div className="grid-radio-button__wrapper" style={{ marginTop: '5px' }}>
          <div className="grid-radio-button">
            {POSITION_OPTIONS.map((option, index) => {
              const { label, value } = option;

              return (
                <Field
                  key={index}
                  invisible={true}
                  name={`setting.${MENU_ID.COLLECTION_HEADER}.contentPosition`}
                  id={value}
                  component={RenderRadioButton2}
                  label={label}
                  disabled={!this.allowEnabled(value)}
                />
              );
            })}
          </div>
        </div>
      </>
    );
  };

  /**
   * Get layout option with translation label
   * @returns
   */
  getImageSizes = () => {
    const { i18n } = this.props;
    return IMAGE_SIZE_OPTIONS.map(option => {
      const { label, value } = option;

      return {
        label: (
          <div>
            <p>{i18n.translate(label)}</p>
            {/* {helpText && <p className="help-text">{helpText}</p>} */}
          </div>
        ),
        value
      };
    });
  };

  /**
   * Get layout option with translation label
   * @returns
   */
  getLayoutOptions = () => {
    const { i18n } = this.props;
    return LAYOUT_OPTIONS.map(option => {
      const { label, value } = option;

      return { label: i18n.translate(label), value };
    });
  };

  render() {
    const { i18n, formValues } = this.props;

    const isEnableCollectionHeader = _.get(
      formValues,
      `setting.${MENU_ID.COLLECTION_HEADER}.isEnable`
    );

    const isOriginal =
      _.get(formValues, `setting.${ELEMENTS.COLLECTION_HEADER_IMAGE}.size`) === 'original';
    const parallaxEffectChecked = _.get(
      formValues,
      `setting.${ELEMENTS.COLLECTION_HEADER_IMAGE}.parallaxEffect`
    );

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeader_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['CollectionHeaderThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          <div className="form-section__item form-section__item--standard-spacing">
            <Field
              name={`setting.${MENU_ID.COLLECTION_HEADER}.isEnable`}
              component={RenderToggleSwitch}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productList_CollectionHeader_Enable'
              )}
            />
          </div>

          {isEnableCollectionHeader && (
            <>
              {this.getPositions()}
              <div className="form-section__item form-section__item--standard-spacing">
                <p className="title" style={{ fontWeight: 600 }}>
                  {i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productList_CollectionHeader_ShowMore'
                  )}
                </p>
              </div>

              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${ELEMENTS.COLLECTION_HEADER_TITLE}.isEnable`}
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.ThemeSetting.Elements.elementOption_CollectionHeader_Title'
                  )}
                />
              </div>

              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${ELEMENTS.COLLECTION_HEADER_PRODUCT_COUNT}.isEnable`}
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.ThemeSetting.Elements.elementOption_ProductCount'
                  )}
                />
              </div>

              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${ELEMENTS.COLLECTION_HEADER_DESCRIPTION}.isEnable`}
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.ThemeSetting.Elements.elementOption_CollectionHeader_Description'
                  )}
                />
              </div>

              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${ELEMENTS.COLLECTION_HEADER_IMAGE}.isEnable`}
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.ThemeSetting.Elements.elementOption_CollectionHeader_Image'
                  )}
                />
              </div>

              <div className="form-section__item" style={{ marginTop: '8px' }}>
                <Field
                  name={`setting.${ELEMENTS.COLLECTION_HEADER_IMAGE}.size`}
                  component={RenderSelect2}
                  options={this.getImageSizes()}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productList_CollectionHeaderImage_Size'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
              </div>

              {!isOriginal && (
                <div className="form-section__item form-section__item--standard-spacing">
                  <Field
                    name={`setting.${ELEMENTS.COLLECTION_HEADER_IMAGE}.parallaxEffect`}
                    component={RenderCheckbox}
                    label={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.collectionHeaderImage_Checkbox_Label'
                    )}
                  />
                </div>
              )}

              {parallaxEffectChecked && !isOriginal && (
                <div className="form-section__item">
                  <p>
                    {i18n.translate(
                      'AdminHTML.ThemeSetting.Form.collectionHeaderImage_Direction_Label'
                    )}
                  </p>
                  <div
                    className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--bordered"
                    style={{ marginTop: '5px' }}>
                    {PARALLAX_DIRECTION_OPTIONS.map((item, index) => (
                      <Field
                        key={index}
                        name={`setting.${ELEMENTS.COLLECTION_HEADER_IMAGE}.directionParallax`}
                        id={item.value}
                        component={RenderRadioButton2}
                        label={i18n.translate(item.label)}
                        disabled={!parallaxEffectChecked || isOriginal}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(ProductListCollectionHeaderForm);
